import { useEffect, useState } from 'react';

export type AsyncResourceState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'error'; readonly message: string };

// #524 -- six workspace components (ProteinWorkspace, PeptideWorkspace, TranscriptWorkspace,
// VariantWorkspace, GeneWorkspace, ObjectWorkspace) each hand-wrote this exact loading/ready/error
// state machine, only the entity name and fallback error string changed between copies. One
// shared implementation instead: calls `fetcher()` whenever `deps` changes, with the standard
// `active`-flag cancellation guard (ignore a resolve/reject after a newer effect run -- a rapid id
// change, or unmount mid-fetch -- already superseded it).
export function useAsyncResource<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[],
  fallbackErrorMessage: string
): AsyncResourceState<T> {
  const [state, setState] = useState<AsyncResourceState<T>>({ status: 'loading' });

  // `deps` is the caller's own dependency list, forwarded as-is; oxlint can't statically verify
  // a spread array the way it can an inline literal, but the contract here is the same as
  // useEffect's own.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    fetcher()
      .then((value) => {
        if (active) setState({ status: 'ready', value });
      })
      .catch((error: unknown) => {
        if (active) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : fallbackErrorMessage
          });
        }
      });
    return () => {
      active = false;
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
